import type { ReactNode } from "react";
import { cn } from "../lib/utils";
import { Avatar } from "./ui/avatar";
import type { Group } from "../api/types";

/**
 * Single-select radiogroup of participant avatar chips — used to pick the
 * payer / payee of a settlement. Mirrors the payer picker in `ExpenseForm`,
 * extracted so the settlement form can reuse it without pulling in the whole
 * (heavy) expense form. Participants in `disabledIds` are dimmed and
 * unselectable (e.g. the person already chosen for the other role).
 */
export function PersonPicker({
  label,
  value,
  people,
  onChange,
  disabledIds,
}: {
  label: string;
  value: string | undefined;
  people: Group["participants"];
  onChange: (personId: string) => void;
  disabledIds?: ReadonlySet<string>;
}): ReactNode {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {people.map((p) => {
        const selected = p.id === value;
        const disabled = disabledIds?.has(p.id) ?? false;
        return (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(p.id)}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-label-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              selected
                ? "border-primary bg-primary text-on-primary"
                : "border-outline-variant/60 bg-surface-container-lowest text-on-surface hover:bg-surface-container",
              disabled &&
                "cursor-not-allowed opacity-40 hover:bg-surface-container-lowest",
            )}
          >
            <Avatar name={p.displayName} size="sm" />
            <span className="max-w-[8rem] truncate">{p.displayName}</span>
          </button>
        );
      })}
    </div>
  );
}
